import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {CompRef, EditLogosParams, IdMapValue, ExecutionIdToMappedId, ImageInfo} from '@wix/document-services-types'
import {type EditLogoParams, fetchEditLogosWorkFlow, type LogoRequestBody} from './requests'
import {
    createLogoUploadConfig,
    createMediaHandler,
    createStaticUrl,
    executeWorkflow,
    ImageContext,
    ImageGenerationParams
} from './utils'
import {ERRORS, LOGS} from '../constants'
import type {Handler} from '../eventHandlerExtension'
import type {ManifestExtensionAPI} from '../../builderComponents/manifest'

const generateAndInjectLogosWorkflow = async <TParams>(
    createExtArgs: CreateExtArgs,
    serverFacade: any,
    componentPointers: CompRef[],
    buildParams: (key: string, logoUrl: string) => TParams,
    fetcher: (serverFacade: any, requestBody: LogoRequestBody<TParams>) => Promise<ExecutionIdToMappedId>,
    {userRequestInfo, onSuccess, onError, caller, errorType}: ImageGenerationParams
): Promise<void> => {
    const {manifest} = createExtArgs.extensionAPI as ManifestExtensionAPI

    const logos: Record<string, TParams> = {}
    const idMap: Record<string, IdMapValue> = {}
    const extraIdMapValuesByRepId: Record<string, IdMapValue[]> = {}
    const uriToRepId: Record<string, string> = {}

    for (const compRef of componentPointers) {
        const logoUri = manifest.getData(compRef, true, [{prop: 'image'}])[0]?.value?.uri
        if (!logoUri) continue

        // TODO: remove condition once editor stores logo URI as a raw file ID consistently E3-9002
        const resolvedUri = logoUri.startsWith('https')
            ? logoUri
            : createStaticUrl(logoUri, {componentPointer: compRef})
        const repId = uriToRepId[resolvedUri]

        if (repId) {
            if (!extraIdMapValuesByRepId[repId]) {
                extraIdMapValuesByRepId[repId] = []
            }
            extraIdMapValuesByRepId[repId].push({pointer: compRef, prop: 'image'})
        } else {
            uriToRepId[resolvedUri] = compRef.id
            logos[compRef.id] = buildParams(compRef.id, resolvedUri)
            idMap[compRef.id] = {pointer: compRef, prop: 'image'}
        }
    }

    const createLogoHandler = (context: ImageContext, mappedId: string): Handler => {
        const extras = extraIdMapValuesByRepId[mappedId] ?? []
        return createMediaHandler(context, mappedId, {
            outputUrlKey: 'new_logo',
            generationLog: LOGS.LOGOS.GENERATION,
            createUploadConfig:
                extras.length > 0
                    ? (logger, callerArg) => {
                          const base = createLogoUploadConfig(logger, callerArg)
                          return {
                              ...base,
                              injectItem: (
                                  extArgs: CreateExtArgs,
                                  idMapValue: IdMapValue,
                                  imageInfo: ImageInfo | null
                              ) => {
                                  base.injectItem(extArgs, idMapValue, imageInfo)
                                  extras.forEach(target => base.injectItem(extArgs, target, imageInfo))
                              }
                          }
                      }
                    : createLogoUploadConfig,
            restoreOnError: false,
            missingUrlMessage: 'Logo url is missing'
        })
    }

    return executeWorkflow(
        createExtArgs,
        serverFacade,
        idMap,
        {},
        {logos, userRequestInfo},
        fetcher,
        {caller, errorType, onSuccess, onError},
        {workflow: LOGS.LOGOS.WORKFLOW, generation: LOGS.LOGOS.GENERATION},
        {
            validation: ERRORS.LOGOS.GENERATION_VALIDATION_ERROR,
            generation: ERRORS.LOGOS.GENERATION,
            emptyMessage: 'Logos generation returned an empty object',
            noItemsMessage: 'No logos generated'
        },
        createLogoHandler
    )
}

export const editLogos = async (
    {serverFacade}: Pick<EnvironmentContext, 'serverFacade'>,
    createExtArgs: CreateExtArgs,
    {
        componentPointers,
        colorPalette,
        headerBg,
        userPrompt,
        businessName,
        industryTerm,
        onSuccess,
        onError,
        userRequestInfo
    }: EditLogosParams
): Promise<void> => {
    return generateAndInjectLogosWorkflow<EditLogoParams>(
        createExtArgs,
        serverFacade,
        componentPointers,
        (_key, logoUrl) => ({
            color_palette: colorPalette,
            header_bg: headerBg,
            logo_url: logoUrl,
            edit_request: userPrompt,
            Business_Name: businessName,
            Industry_Term: industryTerm
        }),
        fetchEditLogosWorkFlow,
        {
            userRequestInfo,
            onSuccess,
            onError,
            caller: 'editLogos',
            errorType: ERRORS.LOGOS.EDIT_LOGOS
        }
    )
}
